export interface ABNTSection {
  number: string;
  title: string;
  level: 1 | 2 | 3;
  content: string;
  subsections?: ABNTSection[];
}

export interface ManualABNTData {
  metadata: {
    title: string;
    subtitle: string;
    version: string;
    date: string;
    author: string;
    keywords: string[];
    license: string;
    year?: string;
    format?: string;
  };
  preTextual: {
    cover: {
      institution: string;
      title: string;
      subtitle: string;
      version: string;
      date: string;
      badge?: string;
    };
    abstract?: string;
  };
  body: {
    sections: ABNTSection[];
  };
  postTextual?: {
    references?: Array<{
      author: string;
      title: string;
      year: string;
      url?: string;
      accessDate?: string;
    }>;
  };
}

export const trackingoneManual: ManualABNTData = {
  metadata: {
    title: "Manual Completo do TrackingONE – Rastreamento GPS em Tempo Real",
    subtitle: "Guia profissional de configuração, operação e análise",
    version: "v1.0",
    date: new Date().toISOString(),
    author: "PortalONE Suite",
    keywords: ["rastreamento", "gps", "tempo real", "geocercas", "comandos remotos", "chips m2m"],
    license: "© PortalONE Suite",
    year: "2025",
    format: "ABNT"
  },

  preTextual: {
    cover: {
      institution: "PortalONE Suite",
      title: "Manual Completo do TrackingONE",
      subtitle: "Rastreamento GPS em Tempo Real",
      version: "v1.0",
      date: "2025",
      badge: "TrackingONE"
    },
    abstract: "O TrackingONE é o módulo de rastreamento GPS do PortalONE Suite, projetado para acompanhar veículos em tempo real, analisar trajetos históricos, criar geocercas inteligentes e executar comandos remotos com segurança. Este manual oferece um guia completo desde a configuração inicial até operações avançadas de análise e integração."
  },

  body: {
    sections: [
      {
        number: "1",
        title: "INTRODUÇÃO",
        level: 1,
        content: `O TrackingONE é o módulo de rastreamento GPS do PortalONE Suite, projetado para acompanhar veículos em tempo real, analisar trajetos históricos, criar geocercas inteligentes e executar comandos remotos com segurança.`,
        subsections: [
          {
            number: "1.1",
            title: "Sobre o TrackingONE",
            level: 2,
            content: `Sistema completo de rastreamento veicular que oferece visibilidade total da frota com dados em tempo real, histórico detalhado e ferramentas de análise avançadas.`
          },
          {
            number: "1.2",
            title: "Público-alvo",
            level: 2,
            content: `- Empresas de logística e transporte
- Frotas corporativas (comerciais e de serviço)
- Operadores de locação e terceirização de veículos
- Gestores de risco e segurança patrimonial`
          },
          {
            number: "1.3",
            title: "Recursos Principais",
            level: 2,
            content: `- Mapa ao vivo com atualização automática
- Histórico de viagens e reprodução de rotas
- Geocercas (circular/poligonal) com alertas
- Comandos remotos (bloqueio/desbloqueio)
- Chips M2M multi-operadora com failover
- Relatórios e dashboards analíticos
- Integração nativa com FleetONE
- API REST e Webhooks em tempo real`
          },
          {
            number: "1.4",
            title: "Requisitos do Sistema",
            level: 2,
            content: `- Navegador moderno (Chrome, Edge, Firefox ou Safari)
- Conexão estável com a internet
- Dispositivo rastreador homologado e SIM M2M
- Permissões adequadas de usuário no PortalONE`
          },
          {
            number: "1.5",
            title: "Estrutura deste Manual",
            level: 2,
            content: `Este manual segue uma progressão lógica: configuração inicial, operação diária, analytics, integrações e suporte.`
          }
        ]
      },
      {
        number: "2",
        title: "PRIMEIROS PASSOS",
        level: 1,
        content: `Configuração inicial e familiarização com a interface do TrackingONE.`,
        subsections: [
          {
            number: "2.1",
            title: "Acesso ao Sistema",
            level: 2,
            content: `1. Acesse \`PortalONE → TrackingONE\`
2. Entre com suas credenciais
3. Caso não possua acesso, solicite ao administrador`
          },
          {
            number: "2.2",
            title: "Interface Principal (Dashboard)",
            level: 2,
            content: `O Dashboard apresenta KPIs, mapa reduzido e alertas recentes, oferecendo uma visão consolidada da operação.`
          },
          {
            number: "2.3",
            title: "Navegação e Menu",
            level: 2,
            content: `- **Mapa ao Vivo**: visão em tempo real
- **Viagens**: histórico e relatórios
- **Geocercas**: criação e gestão
- **Comandos**: envio e auditoria
- **Dispositivos**: SIM, status e configuração
- **Configurações**: preferências e integrações`
          },
          {
            number: "2.4",
            title: "Atalhos e Comandos Rápidos",
            level: 2,
            content: `- \`Ctrl\` + \`K\`: busca global
- Duplo clique no veículo: abre detalhes no mapa`
          },
          {
            number: "2.5",
            title: "Configurações Iniciais",
            level: 2,
            content: `1. Cadastrar veículos e dispositivos
2. Ativar chips M2M e testar conectividade
3. Definir permissões e notificações`
          }
        ]
      },
      {
        number: "3",
        title: "GESTÃO DE VEÍCULOS E DISPOSITIVOS",
        level: 1,
        content: `Gerenciamento completo de veículos GPS e dispositivos rastreadores no TrackingONE.`,
        subsections: [
          {
            number: "3.1",
            title: "Cadastro de Veículos GPS",
            level: 2,
            content: `1. Acesse \`TrackingONE → Dispositivos\`
2. Clique em **+ Novo Veículo**
3. Informe placa, modelo, e centro de custo
4. Salve`
          },
          {
            number: "3.2",
            title: "Configuração de Dispositivos Rastreadores",
            level: 2,
            content: `1. Selecione o fabricante/modelo
2. Informe IMEI/Serial
3. Defina APN e credenciais M2M
4. Teste ping e envio de localização

> **Nota:** Dispositivos homologados possuem presets prontos (APN e comandos padrão).`
          },
          {
            number: "3.3",
            title: "Vinculação Dispositivo-Veículo",
            level: 2,
            content: `1. Acesse o veículo e clique em **Vincular Dispositivo**
2. Escolha o IMEI desejado
3. Confirme`
          },
          {
            number: "3.4",
            title: "Status e Monitoramento de Conectividade",
            level: 2,
            content: `O status indica *Online*, *Offline*, *Sem Sinal* ou *Em Configuração*. A página exibe RSSI, última batida e eventos.`
          },
          {
            number: "3.5",
            title: "Chips M2M Multi-Operadora",
            level: 2,
            content: `Gestão avançada de conectividade com failover automático.`,
            subsections: [
              {
                number: "3.5.1",
                title: "Funcionalidade Failover",
                level: 3,
                content: `Quando a operadora A fica indisponível, o SIM troca para a operadora B automaticamente.`
              },
              {
                number: "3.5.2",
                title: "Gerenciamento de Conectividade",
                level: 3,
                content: `- APN dinâmico por perfil
- Bloqueio/desbloqueio de dados
- Controle de consumo`
              },
              {
                number: "3.5.3",
                title: "Diagnóstico de Sinal",
                level: 3,
                content: `- RSSI (intensidade)
- Cell ID
- Última troca de operadora`
              }
            ]
          }
        ]
      },
      {
        number: "4",
        title: "RASTREAMENTO EM TEMPO REAL",
        level: 1,
        content: `Acompanhamento ao vivo da frota com atualização automática.`,
        subsections: [
          {
            number: "4.1",
            title: "Mapa ao Vivo",
            level: 2,
            content: `Interface principal de rastreamento em tempo real.`,
            subsections: [
              {
                number: "4.1.1",
                title: "Visualização de Frota",
                level: 3,
                content: `Exibe todos os veículos com ícones por status e orientação conforme heading.`
              },
              {
                number: "4.1.2",
                title: "Filtros por Status",
                level: 3,
                content: `- Online, Offline
- Em Movimento, Parado`
              },
              {
                number: "4.1.3",
                title: "Busca por Veículo ou Placa",
                level: 3,
                content: `Use a busca global (\`Ctrl\` + \`K\`) ou o campo no topo do mapa.`
              },
              {
                number: "4.1.4",
                title: "Atualização Automática",
                level: 3,
                content: `Atualização a cada 30 segundos (ou por WebSocket quando disponível).`
              },
              {
                number: "4.1.5",
                title: "Detalhes do Veículo em Tempo Real",
                level: 3,
                content: `- Última posição
- Velocidade atual
- Status de ignição
- Nível de bateria`
              }
            ]
          },
          {
            number: "4.2",
            title: "Informações Disponíveis",
            level: 2,
            content: `- Posição GPS (latitude/longitude)
- Velocidade atual (km/h)
- Direção/orientação
- Status de ignição
- Nível de bateria
- Última atualização`
          },
          {
            number: "4.3",
            title: "Indicadores Visuais no Mapa",
            level: 2,
            content: `- Ícones por status
- Cores e legendas
- Rastros de movimento configuráveis`
          },
          {
            number: "4.4",
            title: "Performance e Latência",
            level: 2,
            content: `- Uptime alvo: 99,9%
- Latência média: < 1s
- Precisão GPS: < 10m`
          }
        ]
      },
      {
        number: "5",
        title: "HISTÓRICO DE VIAGENS",
        level: 1,
        content: `Consulta e análise de trajetos históricos.`,
        subsections: [
          {
            number: "5.1",
            title: "Consulta de Rotas Percorridas",
            level: 2,
            content: `Selecione veículo e período; o mapa plota o trajeto com pontos de telemetria.`
          },
          {
            number: "5.2",
            title: "Reprodução de Trajeto (Playback)",
            level: 2,
            content: `Reproduz o percurso em velocidade ajustável, com marcadores de eventos.`
          },
          {
            number: "5.3",
            title: "Filtros por Data, Período e Veículo",
            level: 2,
            content: `Filtros rápidos (Hoje, 7d, 30d) e intervalo customizado.`
          },
          {
            number: "5.4",
            title: "Análise de Quilometragem",
            level: 2,
            content: `Resumo por período, com cálculo por veículo e por centro de custo.`
          },
          {
            number: "5.5",
            title: "Relatório de Paradas",
            level: 2,
            content: `- Tempo de parada
- Localização
- Duração`
          },
          {
            number: "5.6",
            title: "Exportação de Dados de Viagem",
            level: 2,
            content: `Disponível em CSV, Excel e PDF, conforme filtros aplicados.`
          }
        ]
      },
      {
        number: "6",
        title: "GEOCERCAS INTELIGENTES",
        level: 1,
        content: `Criação e gestão de áreas virtuais com alertas automatizados.`,
        subsections: [
          {
            number: "6.1",
            title: "Conceito e Funcionalidade",
            level: 2,
            content: `Geocercas são áreas virtuais que disparam eventos de entrada, saída e permanência.`
          },
          {
            number: "6.2",
            title: "Criação de Geocercas",
            level: 2,
            content: `Ferramentas de desenho no mapa para definição de áreas.`,
            subsections: [
              {
                number: "6.2.1",
                title: "Geocerca Circular",
                level: 3,
                content: `Defina ponto e raio fixo.`
              },
              {
                number: "6.2.2",
                title: "Geocerca Poligonal",
                level: 3,
                content: `Desenhe contornos personalizados no mapa.`
              }
            ]
          },
          {
            number: "6.3",
            title: "Configuração de Alertas",
            level: 2,
            content: `- Entrada em área
- Saída de área
- Permanência prolongada`
          },
          {
            number: "6.4",
            title: "Ativação/Desativação",
            level: 2,
            content: `Ative/desative individualmente sem perder a configuração.`
          },
          {
            number: "6.5",
            title: "Edição e Exclusão",
            level: 2,
            content: `Atualize vértices, raio e regras a qualquer momento.`
          },
          {
            number: "6.6",
            title: "Validação de Nomes Únicos",
            level: 2,
            content: `Nomes duplicados são bloqueados para evitar confusão em relatórios.`
          },
          {
            number: "6.7",
            title: "Independência do FleetONE",
            level: 2,
            content: `Geocercas do TrackingONE são independentes e não impactam as do FleetONE.`
          }
        ]
      },
      {
        number: "7",
        title: "COMANDOS REMOTOS",
        level: 1,
        content: `Execução segura de comandos críticos via plataforma.`,
        subsections: [
          {
            number: "7.1",
            title: "Bloqueio Remoto de Veículo",
            level: 2,
            content: `Comando para desativação segura do veículo.`,
            subsections: [
              {
                number: "7.1.1",
                title: "Permissões Necessárias",
                level: 3,
                content: `Somente usuários com perfil autorizado podem enviar comandos críticos.`
              },
              {
                number: "7.1.2",
                title: "Procedimento de Segurança",
                level: 3,
                content: `1. Confirme identidade (MFA quando habilitado)
2. Valide contexto (horário/local seguro)
3. Envie comando e acompanhe o retorno`
              },
              {
                number: "7.1.3",
                title: "Log de Auditoria",
                level: 3,
                content: `Toda ação é registrada com usuário, IP, data/hora e resultado.`
              }
            ]
          },
          {
            number: "7.2",
            title: "Desbloqueio Remoto",
            level: 2,
            content: `Procedimento similar ao bloqueio, respeitando a política de segurança.`
          },
          {
            number: "7.3",
            title: "Compatibilidade de Hardware",
            level: 2,
            content: `Verifique se o dispositivo e a instalação suportam corte de ignição seguro.`
          },
          {
            number: "7.4",
            title: "Conformidade e Políticas de Uso",
            level: 2,
            content: `Uso restrito a cenários autorizados por lei e política da empresa.`
          },
          {
            number: "7.5",
            title: "Histórico de Comandos Enviados",
            level: 2,
            content: `Consulta completa com filtros por veículo, usuário e período.`
          }
        ]
      },
      {
        number: "8",
        title: "ALERTAS E NOTIFICAÇÕES",
        level: 1,
        content: `Sistema de alertas e notificações configuráveis.`,
        subsections: [
          {
            number: "8.1",
            title: "Tipos de Alertas Disponíveis",
            level: 2,
            content: `- Excesso de velocidade
- Entrada/saída de geocerca
- Desligamento de ignição não autorizado
- Perda de sinal GPS
- Bateria baixa
- Manutenção preventiva (via FleetONE)`
          },
          {
            number: "8.2",
            title: "Configuração de Regras de Alerta",
            level: 2,
            content: `Defina condições, destinatários e severidade.`
          },
          {
            number: "8.3",
            title: "Canais de Notificação",
            level: 2,
            content: `- In-app
- E-mail
- Webhooks
- Push notifications`
          },
          {
            number: "8.4",
            title: "Gerenciamento de Alertas Ativos",
            level: 2,
            content: `Ative, pause ou ajuste thresholds sem recriar regras.`
          },
          {
            number: "8.5",
            title: "Histórico de Alertas",
            level: 2,
            content: `Registro completo com status de leitura e ações tomadas.`
          }
        ]
      },
      {
        number: "9",
        title: "RELATÓRIOS E ANALYTICS",
        level: 1,
        content: `Geração de relatórios e análise de dados operacionais.`,
        subsections: [
          {
            number: "9.1",
            title: "Relatórios Pré-Configurados",
            level: 2,
            content: `- Quilometragem por veículo
- Tempo de utilização
- Velocidade média/máxima
- Consumo estimado
- Paradas e ociosidade`
          },
          {
            number: "9.2",
            title: "Relatórios Personalizados",
            level: 2,
            content: `Monte relatórios combinando campos (veículo, período, alertas, etc.).`
          },
          {
            number: "9.3",
            title: "Agendamento de Relatórios",
            level: 2,
            content: `Envio recorrente por e-mail para grupos de usuários.`
          },
          {
            number: "9.4",
            title: "Exportação de Dados",
            level: 2,
            content: `- PDF
- Excel (XLSX)
- CSV`
          },
          {
            number: "9.5",
            title: "Indicadores de Performance (KPIs)",
            level: 2,
            content: `- Uso diário por ativo
- Eventos críticos por 1.000 km
- Disponibilidade do dispositivo`
          }
        ]
      },
      {
        number: "10",
        title: "INTEGRAÇÃO COM FLEETONE",
        level: 1,
        content: `Sincronização e compartilhamento de dados com o módulo FleetONE.`,
        subsections: [
          {
            number: "10.1",
            title: "Visão Geral da Integração",
            level: 2,
            content: `Integração nativa com sincronização bidirecional de cadastros e dados operacionais.`
          },
          {
            number: "10.2",
            title: "Sincronização Bidirecional",
            level: 2,
            content: `- Veículos e cadastros
- Motoristas
- Dados de manutenção`
          },
          {
            number: "10.3",
            title: "Alertas Baseados em Quilometragem Real",
            level: 2,
            content: `Envio de odômetro real para planos preventivos do FleetONE.`
          },
          {
            number: "10.4",
            title: "Relatórios Unificados",
            level: 2,
            content: `KPIs combinados entre rastreamento e manutenção.`
          },
          {
            number: "10.5",
            title: "Gestão Centralizada de Documentos",
            level: 2,
            content: `Documentos do veículo acessíveis em ambos os módulos.`
          },
          {
            number: "10.6",
            title: "Analytics Combinado",
            level: 2,
            content: `Camada de BI unificada com dados de frota e telemetria.`
          },
          {
            number: "10.7",
            title: "Configuração da Integração",
            level: 2,
            content: `1. Acesse \`Admin → Integrações\`
2. Ative **FleetONE**
3. Mapeie campos e autorizações`
          }
        ]
      },
      {
        number: "11",
        title: "API E WEBHOOKS",
        level: 1,
        content: `Integração programática com sistemas externos.`,
        subsections: [
          {
            number: "11.1",
            title: "API REST Completa",
            level: 2,
            content: `- Autenticação via token
- Endpoints para veículos, eventos, viagens e geocercas
- Rate limits por plano

\`\`\`json
{
  "GET /api/vehicles": "Lista veículos",
  "GET /api/trips?vehicle_id=": "Viagens por veículo",
  "POST /api/geofences": "Cria geocerca"
}
\`\`\``
          },
          {
            number: "11.2",
            title: "Webhooks em Tempo Real",
            level: 2,
            content: `- Configuração de endpoints de recebimento
- Eventos: *position.created*, *alert.triggered*, *command.status*
- Payload em JSON com retry exponencial`
          },
          {
            number: "11.3",
            title: "Integração com BI Externo",
            level: 2,
            content: `Conecte a ferramentas de BI via exportações agendadas ou API.`
          },
          {
            number: "11.4",
            title: "Documentação da API",
            level: 2,
            content: `Disponível em \`/api/docs\` dentro do PortalONE.`
          },
          {
            number: "11.5",
            title: "Exemplos de Código",
            level: 2,
            content: `\`\`\`javascript
// Exemplo em fetch
fetch('/api/vehicles', { 
  headers: { Authorization: 'Bearer <token>' }
})
  .then(r => r.json())
  .then(console.log)
\`\`\``
          }
        ]
      },
      {
        number: "12",
        title: "SERVIDORES E INFRAESTRUTURA",
        level: 1,
        content: `Arquitetura e disponibilidade da plataforma.`,
        subsections: [
          {
            number: "12.1",
            title: "Arquitetura Multi-Servidor",
            level: 2,
            content: `Separação de serviços (API, Web, Realtime) com escalabilidade horizontal.`
          },
          {
            number: "12.2",
            title: "Monitoramento de Status",
            level: 2,
            content: `Painel em \`/status\` com saúde de componentes.`
          },
          {
            number: "12.3",
            title: "Balanceamento de Carga",
            level: 2,
            content: `Distribuição de requisições e sessões WebSocket.`
          },
          {
            number: "12.4",
            title: "Redundância e Alta Disponibilidade",
            level: 2,
            content: `Replicação de dados e múltiplos nós ativos.`
          },
          {
            number: "12.5",
            title: "SLA e Garantias de Uptime",
            level: 2,
            content: `Meta de disponibilidade 99,9% com janelas de manutenção programadas.`
          }
        ]
      },
      {
        number: "13",
        title: "CONFIGURAÇÕES DO SISTEMA",
        level: 1,
        content: `Personalização e ajustes da plataforma.`,
        subsections: [
          {
            number: "13.1",
            title: "Preferências de Conta",
            level: 2,
            content: `Idioma, fuso horário e formato de data.`
          },
          {
            number: "13.2",
            title: "Gerenciamento de Usuários e Permissões",
            level: 2,
            content: `Convide usuários, atribua papéis e defina escopos de acesso.`
          },
          {
            number: "13.3",
            title: "Configurações de Notificação",
            level: 2,
            content: `Escolha canais e níveis de severidade por evento.`
          },
          {
            number: "13.4",
            title: "Integração com Provedores Externos",
            level: 2,
            content: `Habilite webhooks, chaves de API e Single Sign-On (quando disponível).`
          },
          {
            number: "13.5",
            title: "Logs de Auditoria",
            level: 2,
            content: `Consulta por usuário, IP, entidade e período.`
          },
          {
            number: "13.6",
            title: "Backup e Exportação de Dados",
            level: 2,
            content: `Exportações sob demanda e rotinas automáticas conforme plano.`
          }
        ]
      },
      {
        number: "14",
        title: "SEGURANÇA E CONFORMIDADE",
        level: 1,
        content: `Proteção de dados e conformidade legal.`,
        subsections: [
          {
            number: "14.1",
            title: "Criptografia de Dados",
            level: 2,
            content: `Criptografia em trânsito (TLS) e em repouso para dados sensíveis.`
          },
          {
            number: "14.2",
            title: "Controle de Acesso e Autenticação",
            level: 2,
            content: `RBAC, MFA opcional e políticas de senha.`
          },
          {
            number: "14.3",
            title: "Logs de Auditoria Completos",
            level: 2,
            content: `Registro de operações críticas e relatórios exportáveis.`
          },
          {
            number: "14.4",
            title: "LGPD e Privacidade",
            level: 2,
            content: `Coleta mínima de dados, base legal e direitos do titular.`
          },
          {
            number: "14.5",
            title: "Políticas de Uso de Comandos Remotos",
            level: 2,
            content: `Regras rígidas para envio de bloqueio/desbloqueio e trilhas de auditoria.`
          },
          {
            number: "14.6",
            title: "Certificações e Compliance",
            level: 2,
            content: `Práticas alinhadas a boas referências de mercado.`
          }
        ]
      },
      {
        number: "15",
        title: "PLANOS E LICENCIAMENTO",
        level: 1,
        content: `Modelos de assinatura e recursos por plano.`,
        subsections: [
          {
            number: "15.1",
            title: "Tipos de Plano Disponíveis",
            level: 2,
            content: `Planos com variação de dispositivos, retenção e recursos.`
          },
          {
            number: "15.2",
            title: "Limites por Plano",
            level: 2,
            content: `Limites de API, webhooks e histórico conforme contratação.`
          },
          {
            number: "15.3",
            title: "Teste Grátis (Trial de 30 dias)",
            level: 2,
            content: `Período promocional com recursos selecionados.`
          },
          {
            number: "15.4",
            title: "Upgrade/Downgrade de Plano",
            level: 2,
            content: `Ajuste de plano imediato com cobrança proporcional.`
          },
          {
            number: "15.5",
            title: "Gestão de Assinaturas",
            level: 2,
            content: `Renovação automática e cancelamento pelo PortalONE.`
          },
          {
            number: "15.6",
            title: "Faturamento e Pagamento",
            level: 2,
            content: `Suporte a cartão, boleto e PIX conforme país.`
          }
        ]
      },
      {
        number: "16",
        title: "SOLUÇÃO DE PROBLEMAS",
        level: 1,
        content: `Diagnóstico e resolução de problemas comuns.`,
        subsections: [
          {
            number: "16.1",
            title: "Dispositivo Offline",
            level: 2,
            content: `- Verifique alimentação elétrica
- Confirme cobertura M2M e APN
- Refaça pareamento com o veículo`
          },
          {
            number: "16.2",
            title: "Posição GPS Imprecisa",
            level: 2,
            content: `- Cheque antena e obstruções
- Aguarde fix inicial (TTFF)`
          },
          {
            number: "16.3",
            title: "Comandos Remotos Não Executados",
            level: 2,
            content: `- Dispositivo online?
- Permissões do usuário
- Compatibilidade de hardware`
          },
          {
            number: "16.4",
            title: "Alertas Não Recebidos",
            level: 2,
            content: `- Regras ativas e thresholds corretos
- Verifique canal (e-mail/spam, push, webhooks)`
          },
          {
            number: "16.5",
            title: "Erro de Sincronização com FleetONE",
            level: 2,
            content: `Revise credenciais, mapeamento de campos e filas de integração.`
          },
          {
            number: "16.6",
            title: "Performance Lenta no Mapa",
            level: 2,
            content: `Reduza o range de tempo, filtre veículos ou use clusters.`
          },
          {
            number: "16.7",
            title: "Problemas de Login/Autenticação",
            level: 2,
            content: `Redefina senha, verifique MFA e contate o administrador.`
          }
        ]
      },
      {
        number: "17",
        title: "PERGUNTAS FREQUENTES (FAQ)",
        level: 1,
        content: `Respostas para as dúvidas mais comuns sobre o TrackingONE.`,
        subsections: [
          {
            number: "17.1",
            title: "Como funciona o comando remoto para desligar um veículo?",
            level: 2,
            content: `Envia instrução segura ao módulo, respeitando permissões e auditoria.`
          },
          {
            number: "17.2",
            title: "Os chips M2M são multi-operadora?",
            level: 2,
            content: `Sim, com failover automático conforme disponibilidade.`
          },
          {
            number: "17.3",
            title: "Posso integrar os dados ao meu BI?",
            level: 2,
            content: `Sim, via API, webhooks e exportações.`
          },
          {
            number: "17.4",
            title: "Como é feita a integração com FleetONE?",
            level: 2,
            content: `Por sincronização bidirecional e compartilhamento de KPIs.`
          },
          {
            number: "17.5",
            title: "Qual a precisão do GPS?",
            level: 2,
            content: `< 10 metros em condições ideais.`
          },
          {
            number: "17.6",
            title: "Quantos dispositivos posso cadastrar?",
            level: 2,
            content: `De acordo com o plano contratado.`
          },
          {
            number: "17.7",
            title: "Como funciona o período de teste grátis?",
            level: 2,
            content: `30 dias com recursos selecionados.`
          },
          {
            number: "17.8",
            title: "Preciso ter FleetONE para usar TrackingONE?",
            level: 2,
            content: `Não, são módulos independentes.`
          },
          {
            number: "17.9",
            title: "Os dados ficam armazenados por quanto tempo?",
            level: 2,
            content: `Conforme retenção do plano.`
          },
          {
            number: "17.10",
            title: "Como funciona o failover dos chips M2M?",
            level: 2,
            content: `Troca automática de operadora sob perda de sinal/serviço.`
          }
        ]
      },
      {
        number: "18",
        title: "SUPORTE E CONTATO",
        level: 1,
        content: `Canais de atendimento e recursos de ajuda.`,
        subsections: [
          {
            number: "18.1",
            title: "Canais de Suporte",
            level: 2,
            content: `- Suporte 24/7
- Chat in-app
- E-mail: suporte@trackingone.com.br
- WhatsApp (quando habilitado)`
          },
          {
            number: "18.2",
            title: "Base de Conhecimento",
            level: 2,
            content: `Artigos e tutoriais na seção **Manuais** do PortalONE.`
          },
          {
            number: "18.3",
            title: "Status do Sistema",
            level: 2,
            content: `Acompanhe disponibilidade em \`/status\`.`
          },
          {
            number: "18.4",
            title: "Agendamento de Demonstração",
            level: 2,
            content: `Solicite *demo* pelo menu **Contato** na landing do TrackingONE.`
          },
          {
            number: "18.5",
            title: "Feedback e Sugestões",
            level: 2,
            content: `Envie ideias pelo formulário in-app de **Feedback**.`
          }
        ]
      },
      {
        number: "GLOSSÁRIO",
        title: "GLOSSÁRIO",
        level: 1,
        content: `**API**: Application Programming Interface

**BI**: Business Intelligence

**Failover**: Redundância automática entre redes

**Geocerca**: Perímetro virtual geográfico

**GNSS**: Global Navigation Satellite System

**GPS**: Global Positioning System

**KPI**: Key Performance Indicator

**Latência**: Tempo de resposta do sistema

**M2M**: Machine-to-Machine

**Payload**: Dados enviados em requisição/webhook

**Playback**: Reprodução de trajeto histórico

**REST**: Representational State Transfer

**SIM**: Subscriber Identity Module

**SLA**: Service Level Agreement

**Uptime**: Tempo de disponibilidade do sistema

**Webhook**: Notificação automatizada via HTTP`
      },
      {
        number: "REFERÊNCIAS",
        title: "REFERÊNCIAS",
        level: 1,
        content: `- Portal PortalONE Suite: \`https://www.portalone.adm.br\`
- Landing Page TrackingONE: \`/trackingone\`
- Documentação API: \`/api/docs\`
- Status do Sistema: \`/status\`
- Guia UTM Marketing: Manual disponível na área de suporte
- Manual FleetONE: Manual disponível na área de suporte`
      }
    ]
  }
};
